export const AUTH_SHELL_SNAPSHOT_KEY = 'stocktag_auth_shell_snapshot';

const toFiniteNumber = (value, fallback = 0) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
};

const CREDIT_PACK_HISTORY_KINDS = new Set([
  'credit_pack',
  'credit_pack_purchased',
  'credit_topup',
  'additional_credits',
  'additional_package'
]);

const hasVerifiedPrepaidRelationship = (value) => {
  if (value?.prepaidCustomer === true || value?.hasPurchasedCreditPack === true) {
    return true;
  }

  const purchaseHistory = Array.isArray(value?.purchaseHistory) ? value.purchaseHistory : [];
  return purchaseHistory.some((entry) => {
    const kind = String(entry?.kind || entry?.type || '').trim().toLowerCase();
    return CREDIT_PACK_HISTORY_KINDS.has(kind);
  });
};

// Only a profile locked for deletion carries this field, so the lock screen is
// shown on reload before the Firestore profile arrives. Other snapshots are
// unchanged.
const ACCOUNT_DELETION_LOCKED_STATUSES = new Set(['scheduled', 'purging']);

const normalizeSnapshotAccountDeletion = (value) => {
  const status = String(value?.accountDeletion?.status || '').trim().toLowerCase();
  if (!ACCOUNT_DELETION_LOCKED_STATUSES.has(status)) return null;
  return {
    status,
    scheduledForMs: Math.max(0, toFiniteNumber(value?.accountDeletion?.scheduledForMs, 0))
  };
};

const normalizeSnapshotUser = (value) => {
  const uid = String(value?.uid || '').trim();
  if (!uid) return null;
  const hasDualLayerRightsVerificationField = Object.prototype.hasOwnProperty.call(
    value || {},
    'dualLayerRightsVerificationEnabled'
  );
  const accountDeletion = normalizeSnapshotAccountDeletion(value);

  return {
    uid,
    email: String(value?.email || '').trim() || null,
    displayName: String(value?.displayName || '').trim() || null,
    photoURL: String(value?.photoURL || '').trim() || null,
    credits: toFiniteNumber(value?.credits, 0),
    totalProcessed: toFiniteNumber(value?.totalProcessed, 0),
    createdAtMs: Math.max(0, toFiniteNumber(value?.createdAtMs, 0)),
    plan: String(value?.plan || 'free').trim() || 'free',
    subscriptionStatus: String(value?.subscriptionStatus || 'inactive').trim() || 'inactive',
    prepaidCustomer: hasVerifiedPrepaidRelationship(value),
    isAdmin: Boolean(value?.isAdmin),
    willRenew: Boolean(value?.willRenew),
    freeByokDailyUsage: value?.freeByokDailyUsage || null,
    ...(hasDualLayerRightsVerificationField
      ? { dualLayerRightsVerificationEnabled: value?.dualLayerRightsVerificationEnabled === true }
      : {}),
    ...(accountDeletion ? { accountDeletion } : {})
  };
};

export const readAuthShellSnapshot = () => {
  if (typeof window === 'undefined') return null;

  try {
    const raw = localStorage.getItem(AUTH_SHELL_SNAPSHOT_KEY);
    if (!raw) return null;
    return normalizeSnapshotUser(JSON.parse(raw));
  } catch {
    return null;
  }
};

export const writeAuthShellSnapshot = (user) => {
  if (typeof window === 'undefined') return;

  try {
    const normalized = normalizeSnapshotUser(user);
    if (!normalized) {
      localStorage.removeItem(AUTH_SHELL_SNAPSHOT_KEY);
      return;
    }

    localStorage.setItem(AUTH_SHELL_SNAPSHOT_KEY, JSON.stringify(normalized));
  } catch {
    // noop
  }
};

export const mergeSnapshotWithFirebaseUser = (snapshotUser, firebaseUser) => {
  if (!firebaseUser?.uid) return null;
  const firebaseUid = String(firebaseUser.uid).trim();
  const snapshotUid = String(snapshotUser?.uid || '').trim();
  const matchingSnapshot = snapshotUid && snapshotUid === firebaseUid
    ? snapshotUser
    : null;

  return normalizeSnapshotUser({
    ...matchingSnapshot,
    uid: firebaseUid,
    email: firebaseUser.email || matchingSnapshot?.email || '',
    displayName: firebaseUser.displayName || matchingSnapshot?.displayName || '',
    photoURL: firebaseUser.photoURL || matchingSnapshot?.photoURL || ''
  });
};
